<script setup lang="ts">
import type { ImportEntry } from "@/types";

import { computed, ref, watch } from "vue";
import { useDisplay } from "vuetify";

import GuildIcon from "@/components/GuildIcon.vue";

import { validateEntry } from "@/composable/useDictionaryImport";

import { useDictionaryStore } from "@/stores/useDictionaryStore";
import { useGuildStore } from "@/stores/useGuildStore";
import { useSnackbarStore } from "@/stores/useSnackbarStore";
import { useVGuildStore } from "@/stores/useVGuildStore";

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const { mobile } = useDisplay();
const dictionaryStore = useDictionaryStore();
const guildStore = useGuildStore();
const vGuildStore = useVGuildStore();
const snackbarStore = useSnackbarStore();

// ─── ギルド選択 ──────────────────────────────────────────────
const selectedGuildId = ref<string | null>(null);

const otherGuilds = computed(() =>
  guildStore.managedJoinedGuilds.filter((g) => g.id !== vGuildStore.vGuild.guild_id),
);

// ─── フェッチ結果 ─────────────────────────────────────────────
const entries = ref<ImportEntry[]>([]);
const loading = ref(false);

watch(selectedGuildId, async (guildId) => {
  entries.value = [];
  if (!guildId) return;

  loading.value = true;
  const result = await dictionaryStore.fetchFromOtherGuild(guildId);
  loading.value = false;
  if (result) {
    entries.value = result.map(({ before, after }) => ({ before, after }));
  }
});

// ─── バリデーション済みエントリ ──────────────────────────────────
const validatedEntries = computed(() => entries.value.map(validateEntry));
const invalidCount = computed(() => validatedEntries.value.filter((e) => !e.valid).length);

// ─── チェックボックス選択 ────────────────────────────────────────
const selectedIndices = ref<Set<number>>(new Set());

watch(entries, () => {
  selectedIndices.value = new Set(validatedEntries.value.flatMap((e, i) => (e.valid ? [i] : [])));
});

const allValidCount = computed(() => validatedEntries.value.filter((e) => e.valid).length);
const headerChecked = computed(
  () => allValidCount.value > 0 && selectedIndices.value.size === allValidCount.value,
);
const headerIndeterminate = computed(
  () => selectedIndices.value.size > 0 && selectedIndices.value.size < allValidCount.value,
);

const toggleHeader = () => {
  if (headerChecked.value) {
    selectedIndices.value = new Set();
  } else {
    selectedIndices.value = new Set(validatedEntries.value.flatMap((e, i) => (e.valid ? [i] : [])));
  }
};

const toggleRow = (index: number) => {
  const next = new Set(selectedIndices.value);
  if (next.has(index)) {
    next.delete(index);
  } else {
    next.add(index);
  }
  selectedIndices.value = next;
};

const validEntries = computed(() =>
  validatedEntries.value
    .filter((e, i) => e.valid && selectedIndices.value.has(i))
    .map(({ before, after }) => ({ before, after })),
);

// ─── 件数チェック ─────────────────────────────────────────────
const maxCount = computed(() => (vGuildStore.boostCount > 0 ? 300 : 50));
const existingBeforeSet = computed(
  () => new Set(dictionaryStore.dictionaries.map((d) => d.before)),
);
const selectedNewCount = computed(
  () => validEntries.value.filter((e) => !existingBeforeSet.value.has(e.before)).length,
);
const willExceedLimit = computed(
  () => existingBeforeSet.value.size + selectedNewCount.value > maxCount.value,
);

// ─── インポート実行 ──────────────────────────────────────────
const importing = ref(false);

const doImport = async () => {
  if (validEntries.value.length === 0) return;
  importing.value = true;
  try {
    const result = await dictionaryStore.importDictionary(validEntries.value);
    if (result) {
      snackbarStore.addTopRight({
        color: "success",
        text: `インポート完了: 新規 ${result.imported} 件 / 上書き ${result.updated} 件`,
        timeout: 3000,
      });
      emit("update:modelValue", false);
    }
  } finally {
    importing.value = false;
  }
};

const close = () => {
  selectedGuildId.value = null;
  emit("update:modelValue", false);
};

defineExpose({ doImport, validEntries, dictionaryStore, selectedGuildId, entries });
</script>

<template>
  <v-dialog
    :model-value="props.modelValue"
    class="p-dictionary__import-dialog"
    :fullscreen="mobile"
    max-width="760"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="bg-background">
      <v-toolbar theme="dark">
        <v-btn
          icon
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>辞書インポート</v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn
            color="secondary"
            :disabled="validEntries.length === 0 || willExceedLimit || importing"
            :loading="importing"
            @click="doImport"
          >
            確定
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <v-container>
        <!-- サーバー選択 -->
        <v-alert
          v-if="otherGuilds.length === 0"
          type="info"
          class="mb-4"
          density="compact"
        >
          管理しているサーバーが他にありません
        </v-alert>
        <v-select
          v-else
          v-model="selectedGuildId"
          :items="otherGuilds"
          item-title="name"
          item-value="id"
          label="サーバーを選択"
          class="mb-4"
          hide-details
        >
          <template #item="{ item, props: itemProps }">
            <v-list-item
              v-bind="itemProps"
              title=""
            >
              <template #prepend>
                <GuildIcon
                  :guild="item.raw"
                  :size="40"
                  :hoverable="false"
                  class="mr-3"
                />
              </template>
              {{ item.title }}
            </v-list-item>
          </template>
          <template #selection="{ item }">
            <GuildIcon
              :guild="item.raw"
              :size="32"
              :hoverable="false"
              class="mr-2"
            />
            {{ item.title }}
          </template>
        </v-select>

        <v-progress-linear
          v-if="loading"
          indeterminate
          class="mb-3"
        />

        <!-- エラー・警告 -->
        <v-alert
          v-if="willExceedLimit"
          type="error"
          class="mb-3"
          density="compact"
        >
          登録件数が上限（{{ maxCount }} 件）を超えます（現在: {{ existingBeforeSet.size }} 件 /
          新規: {{ selectedNewCount }} 件）。 インポートを続行できません。
        </v-alert>
        <v-alert
          v-else-if="invalidCount > 0"
          type="warning"
          class="mb-3"
          density="compact"
        >
          {{ invalidCount }} 件のエントリにエラーがあります。エラー行はスキップされます。
        </v-alert>

        <!-- 辞書なし -->
        <v-alert
          v-if="selectedGuildId && !loading && entries.length === 0"
          type="info"
          class="mb-3"
          density="compact"
        >
          選択したサーバーには辞書が登録されていません
        </v-alert>

        <!-- プレビューテーブル -->
        <template v-if="selectedGuildId && !loading && entries.length > 0">
          <div class="text-caption mb-1">
            プレビュー（{{ validatedEntries.length }} 件 / 選択: {{ validEntries.length }} 件）
          </div>
          <v-table
            class="bg-background"
            density="compact"
            fixed-header
            height="360"
          >
            <thead>
              <tr class="bg-surface-lighten-1">
                <th style="width: 40px">
                  <v-checkbox
                    :model-value="headerChecked"
                    :indeterminate="headerIndeterminate"
                    density="compact"
                    hide-details
                    @click.prevent="toggleHeader"
                  />
                </th>
                <th>変換前</th>
                <th>変換後</th>
                <th>状態</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(entry, i) in validatedEntries"
                :key="i"
                :class="{ 'text-error': !entry.valid }"
              >
                <td>
                  <v-checkbox
                    :model-value="selectedIndices.has(i)"
                    :disabled="!entry.valid"
                    density="compact"
                    hide-details
                    @update:model-value="toggleRow(i)"
                  />
                </td>
                <td>{{ entry.before }}</td>
                <td>{{ entry.after }}</td>
                <td>
                  <span
                    v-if="!entry.valid"
                    class="text-error text-caption"
                    >{{ entry.error }}</span
                  >
                  <span
                    v-else-if="existingBeforeSet.has(entry.before)"
                    class="text-warning text-caption"
                    >上書き</span
                  >
                  <span
                    v-else
                    class="text-success text-caption"
                    >新規</span
                  >
                </td>
              </tr>
            </tbody>
          </v-table>
        </template>
      </v-container>
    </v-card>
  </v-dialog>
</template>
